import { X, Command, Search, PanelLeft, Palette } from 'lucide-react';

/**
 * Keyboard Shortcuts Help Modal
 * Displays all available keyboard shortcuts for accessibility
 */
const KeyboardShortcutsHelp = ({ open, onClose }) => {
  if (!open) return null;

  const shortcuts = [
    {
      category: 'Navigation',
      items: [
        { keys: ['Ctrl', 'K'], description: 'Open search', icon: Search },
        { keys: ['Ctrl', 'B'], description: 'Toggle sidebar', icon: PanelLeft },
        { keys: ['Tab'], description: 'Navigate forward', icon: null },
        { keys: ['Shift', 'Tab'], description: 'Navigate backward', icon: null },
        { keys: ['Esc'], description: 'Close dialogs', icon: null },
      ],
    },
    {
      category: 'Appearance',
      items: [
        { keys: ['Ctrl', 'Shift', 'T'], description: 'Toggle theme', icon: Palette },
      ],
    },
    {
      category: 'Help',
      items: [
        { keys: ['Ctrl', '/'], description: 'Show this help', icon: null },
      ],
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white dark:bg-dark-800 rounded-xl shadow-2xl dark:shadow-dark-soft-xl border border-gray-200 dark:border-dark-700">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
            <div>
              <h2
                id="shortcuts-title"
                className="text-2xl font-bold text-gray-900 dark:text-gray-100"
              >
                Keyboard Shortcuts
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Boost your productivity with these shortcuts
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
              aria-label="Close shortcuts help"
            >
              <X size={20} className="text-gray-500 dark:text-gray-400" aria-hidden="true" />
            </button>
          </div>

          {/* Shortcuts List */}
          <div className="p-6 space-y-8">
            {shortcuts.map((category) => (
              <div key={category.category}>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                  {category.category}
                </h3>
                <div className="space-y-3">
                  {category.items.map((shortcut, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {shortcut.icon && (
                          <shortcut.icon
                            size={18}
                            className="text-brand-600 dark:text-brand-400"
                            aria-hidden="true"
                          />
                        )}
                        <span className="text-gray-700 dark:text-gray-300">
                          {shortcut.description}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIdx) => (
                          <div key={keyIdx} className="flex items-center gap-1">
                            {keyIdx > 0 && (
                              <span className="text-gray-400 dark:text-gray-500 text-xs">
                                +
                              </span>
                            )}
                            <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-dark-600 border border-gray-300 dark:border-dark-500 rounded shadow-sm">
                              {key === 'Ctrl' && (
                                <span className="inline-flex items-center gap-1">
                                  <Command size={12} aria-hidden="true" />
                                  <span className="sr-only">Control or Command</span>
                                </span>
                              )}
                              {key !== 'Ctrl' && key}
                            </kbd>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900 rounded-b-xl">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <strong>Note:</strong> On Mac, use <kbd className="px-1 py-0.5 text-xs bg-gray-200 dark:bg-dark-700 rounded">⌘ Cmd</kbd> instead of <kbd className="px-1 py-0.5 text-xs bg-gray-200 dark:bg-dark-700 rounded">Ctrl</kbd>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
